import { type EntitySnapshot, TICK_INTERVAL_MS } from '@dreamgame/shared';
import { signal } from '@preact/signals';

// ============================================================================
// Network State - Signals-based state management for NetworkClient
// ============================================================================
// Note: High-level connection status signals are in ./connection.ts
// This file manages detailed network protocol state
// ============================================================================

// Constants
export const PING_INTERVAL_MS = 2000;
export const RTT_SAMPLE_COUNT = 10;

// Reconnection configuration (immutable)
export interface ReconnectConfig {
  readonly enabled: boolean;
  readonly maxAttempts: number;
  readonly baseDelayMs: number;
  readonly maxDelayMs: number;
}

export const DEFAULT_RECONNECT_CONFIG: ReconnectConfig = {
  enabled: true,
  maxAttempts: 10,
  baseDelayMs: 1000,
  maxDelayMs: 30000,
};

// ============================================================================
// Core Network Signals
// ============================================================================

// Input sequence counter
export const inputSequence = signal(0);

// Server tick
export const serverTick = signal(0);
export const serverTickUpdatedAtMs = signal(0);
export const serverTickIntervalMs = signal(TICK_INTERVAL_MS);

// Last acknowledged input from server
export const lastAcknowledgedInput = signal(0);

// Reconnection enabled flag (separate from connection.ts reconnectAttempts)
export const reconnectEnabled = signal(true);

// ============================================================================
// Ping/Latency Signals
// ============================================================================

export const lastPingTime = signal(0);
export const rttMs = signal(0);
export const jitterMs = signal(0);

// RTT samples (kept as array in signal for calculation)
export const rttSamples = signal<readonly number[]>([]);

// ============================================================================
// Entity State (for delta compression)
// ============================================================================

// Using signal with Map for entity states
export const entityStates = signal<ReadonlyMap<number, EntitySnapshot>>(new Map());

// ============================================================================
// Actions
// ============================================================================

export const incrementInputSequence = (): number => {
  const current = inputSequence.value;
  inputSequence.value = current + 1;
  return current;
};

export const updateServerTick = ({ tick }: { tick: number }): void => {
  serverTick.value = tick;
  serverTickUpdatedAtMs.value = performance.now();
};

export const updateServerTickRate = ({ tickRate }: { tickRate: number }): void => {
  if (!Number.isFinite(tickRate) || tickRate <= 0) {
    return;
  }
  serverTickIntervalMs.value = 1000 / tickRate;
};

export const updateLastAcknowledgedInput = ({ sequence }: { sequence: number }): void => {
  lastAcknowledgedInput.value = sequence;
};

export const setReconnectEnabled = ({ enabled }: { enabled: boolean }): void => {
  reconnectEnabled.value = enabled;
};

export const updatePingTime = ({ time }: { time: number }): void => {
  lastPingTime.value = time;
};

export const addRttSample = ({ rtt }: { rtt: number }): void => {
  const samples = [...rttSamples.value, rtt];
  // Keep only the last RTT_SAMPLE_COUNT samples
  if (samples.length > RTT_SAMPLE_COUNT) {
    samples.shift();
  }
  rttSamples.value = samples;

  // Calculate average RTT
  const avgRtt = samples.reduce((a, b) => a + b, 0) / samples.length;
  rttMs.value = avgRtt;

  // Calculate jitter (standard deviation)
  if (samples.length > 1) {
    const variance = samples.reduce((sum, val) => sum + (val - avgRtt) ** 2, 0) / samples.length;
    jitterMs.value = Math.sqrt(variance);
  }
};

export const resetLatencyStats = (): void => {
  rttSamples.value = [];
  rttMs.value = 0;
  jitterMs.value = 0;
};

// ============================================================================
// Entity State Actions
// ============================================================================

export const setEntityState = ({
  networkId,
  entity,
}: {
  networkId: number;
  entity: EntitySnapshot;
}): void => {
  const next = new Map(entityStates.value);
  next.set(networkId, entity);
  entityStates.value = next;
};

export const getEntityState = ({
  networkId,
}: {
  networkId: number;
}): EntitySnapshot | undefined => {
  return entityStates.value.get(networkId);
};

export const deleteEntityState = ({ networkId }: { networkId: number }): void => {
  const next = new Map(entityStates.value);
  next.delete(networkId);
  entityStates.value = next;
};

export const clearEntityStates = (): void => {
  entityStates.value = new Map();
};

// ============================================================================
// Reset All Network State
// ============================================================================

export const resetNetworkState = (): void => {
  inputSequence.value = 0;
  serverTick.value = 0;
  serverTickUpdatedAtMs.value = 0;
  serverTickIntervalMs.value = TICK_INTERVAL_MS;
  lastAcknowledgedInput.value = 0;
  reconnectEnabled.value = true;
  resetLatencyStats();
  clearEntityStates();
};
